import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { APPS, type AppId } from "@/lib/apps";
import { useProfile } from "@/lib/profile/useProfile";
import { useFounder } from "@/lib/founder/useFounder";
import { isAdult, MIN_AGE } from "@/lib/auth/age";
import {
  type FriendshipLane,
  type ProfilePrompt,
  MIN_PROMPTS,
} from "@/lib/profile/types";
import PhotoGrid from "@/components/profile/PhotoGrid";
import PromptsPicker from "@/components/profile/PromptsPicker";
import VoiceRecorder from "@/components/profile/VoiceRecorder";
import { toast } from "sonner";

const ND_OPTIONS = [
  "ADHD",
  "Autism",
  "Dyslexia",
  "Dyspraxia",
  "Tourette's",
  "OCD",
  "Sensory Processing",
  "Other",
];

const LANES: { value: FriendshipLane; label: string; desc: string }[] = [
  { value: "dating", label: "Dating", desc: "Romantic connection." },
  { value: "friendship", label: "Friendship", desc: "Platonic only." },
  { value: "both", label: "Both", desc: "Open to either." },
  { value: "mentorship", label: "Mentorship", desc: "Guidance & growth." },
];

const Onboarding = () => {
  const { profile, loading, update } = useProfile();
  const { isFounder, loading: founderLoading } = useFounder();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [primaryApp, setPrimaryApp] = useState<AppId | "">("");
  const [displayName, setDisplayName] = useState("");
  const [pronouns, setPronouns] = useState("");
  const [dob, setDob] = useState("");
  const [legalFirstName, setLegalFirstName] = useState("");
  const [legalLastName, setLegalLastName] = useState("");
  const [bio, setBio] = useState("");
  const [genderIdentity, setGenderIdentity] = useState("");
  const [sexualOrientation, setSexualOrientation] = useState("");
  const [neurodivergence, setNeurodivergence] = useState<string[]>([]);
  const [photos, setPhotos] = useState<string[]>([]);
  const [prompts, setPrompts] = useState<ProfilePrompt[]>([]);
  const [voiceUrl, setVoiceUrl] = useState<string | null>(null);
  const [lane, setLane] = useState<FriendshipLane>("dating");
  const [mentorIntent, setMentorIntent] = useState<"apply" | "browse" | "skip" | null>(
    null,
  );

  useEffect(() => {
    if (profile) {
      setPrimaryApp((profile.primary_app as AppId) ?? "");
      setDisplayName(profile.display_name ?? "");
      setPronouns(profile.pronouns ?? "");
      setDob(profile.dob ?? "");
      setLegalFirstName(profile.legal_first_name ?? "");
      setLegalLastName(profile.legal_last_name ?? "");
      setBio(profile.bio ?? "");
      setGenderIdentity(profile.gender_identity ?? "");
      setSexualOrientation(profile.sexual_orientation ?? "");
      setNeurodivergence(profile.neurodivergence ?? []);
      setPhotos(profile.photos ?? []);
      setPrompts(profile.prompts ?? []);
      setVoiceUrl(profile.voice_intro_url);
      setLane(profile.friendship_lane ?? "dating");

      if (profile.onboarding_completed_at) {
        navigate("/account", { replace: true });
      }
    }
  }, [profile, navigate]);

  useEffect(() => {
    if (!founderLoading && isFounder) {
      navigate("/account", { replace: true });
    }
  }, [founderLoading, isFounder, navigate]);

  if (loading || founderLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  const toggleND = (item: string) => {
    setNeurodivergence((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item],
    );
  };

  const totalSteps = 7;
  const stepLabel = `Step ${step + 1} of ${totalSteps}`;
  const isAttuned = profile?.tier === "tier_attuned";
  const isPartner = profile?.kyc_provider === "partner_code";

  const finish = async () => {
    if (!isFounder && !isAdult(dob)) {
      toast.error(`You must be ${MIN_AGE}+ to join.`);
      return;
    }
    if (!primaryApp || !displayName) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (!isFounder && (!legalFirstName.trim() || !legalLastName.trim())) {
      toast.error("Legal first and last name are required.");
      setStep(1);
      return;
    }
    if (!isFounder && photos.length < 1) {
      toast.error("Please add at least one photo.");
      setStep(2);
      return;
    }
    if (!isFounder && prompts.filter((p) => p.answer.trim()).length < MIN_PROMPTS) {
      toast.error(`Please answer at least ${MIN_PROMPTS} prompts.`);
      setStep(3);
      return;
    }
    setBusy(true);
    const patch: Record<string, unknown> = {
      primary_app: primaryApp,
      active_app: primaryApp,
      display_name: displayName,
      pronouns: pronouns || null,
      dob,
      legal_first_name: legalFirstName.trim(),
      legal_last_name: legalLastName.trim(),
      bio: bio || null,
      photos,
      prompts: prompts.filter((p) => p.answer.trim()),
      voice_intro_url: voiceUrl,
      friendship_lane: lane,
      onboarding_completed_at: new Date().toISOString(),
    };
    if (primaryApp === "fryqnc") {
      patch.gender_identity = genderIdentity || null;
      patch.sexual_orientation = sexualOrientation || null;
    }
    if (primaryApp === "mindlynq") {
      patch.neurodivergence = neurodivergence.length ? neurodivergence : null;
    }
    const { error } = await update(patch);
    setBusy(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success("Welcome to Wavlynq.");
    // Mentorship routing: Attuned members who opt in go straight into the
    // mentor flow. Anyone (any tier) who picked "browse mentors" goes to
    // the mentor browse page. Otherwise, off to their primary app.
    if (isAttuned && mentorIntent === "apply") {
      navigate("/mentor-apply", { replace: true });
      return;
    }
    if (mentorIntent === "browse") {
      navigate("/mentors", { replace: true });
      return;
    }
    navigate(`/${primaryApp}`, { replace: true });
  };

  const skipOnboarding = async () => {
    setBusy(true);
    const app = primaryApp || profile?.primary_app || profile?.active_app;
    await update({
      onboarding_completed_at: new Date().toISOString(),
      ...(app ? { primary_app: app, active_app: app } : {}),
    });
    setBusy(false);
    toast.success("Welcome to Wavlynq.");
    navigate(app ? `/${app}` : "/", { replace: true });
  };

  const next = () => setStep((s) => Math.min(s + 1, totalSteps - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canAdvance = (() => {
    switch (step) {
      case 0: return !!primaryApp;
      case 1: return !!displayName && (isFounder || (!!dob && !!legalFirstName.trim() && !!legalLastName.trim()));
      case 2: return isFounder || photos.length >= 1;
      case 3: return isFounder || prompts.filter((p) => p.answer.trim()).length >= MIN_PROMPTS;
      case 4: return true; // voice optional
      case 5: return true;
      case 6: return mentorIntent !== null;
      default: return false;
    }
  })();

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex-1 mx-auto w-full max-w-2xl px-6 py-16">
        <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
          Onboarding · {stepLabel}
        </p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight">Set up your profile</h1>

        {/* progress bar */}
        <div className="mt-6 h-1 w-full rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full bg-foreground transition-all"
            style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Step 0: pick app */}
        {step === 0 && (
          <section className="mt-10 space-y-6">
            <div>
              <h2 className="text-xl font-semibold">Choose your community</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                You can switch later, but each membership is community-specific. Want all of them? Upgrade to <span className="text-foreground font-medium">Attuned</span> anytime.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {APPS.map((app) => {
                const selected = primaryApp === app.id;
                return (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => setPrimaryApp(app.id)}
                    className={`text-left rounded-2xl border p-5 transition-colors ${
                      selected ? "border-foreground bg-secondary" : "border-border hover:bg-secondary/40"
                    }`}
                  >
                    <img src={app.icon} alt="" className="h-10 w-10" />
                    <p className="mt-4 font-semibold">{app.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{app.community}</p>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Step 1: basics + identity */}
        {step === 1 && (
          <section className="mt-10 space-y-6">
            <div>
              <h2 className="text-xl font-semibold">The basics</h2>
              <p className="mt-2 text-sm text-muted-foreground">This is what other members will see.</p>
            </div>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="lfn">Legal first name *</Label>
                  <Input id="lfn" required maxLength={100} value={legalFirstName}
                         onChange={(e) => setLegalFirstName(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="lln">Legal last name *</Label>
                  <Input id="lln" required maxLength={100} value={legalLastName}
                         onChange={(e) => setLegalLastName(e.target.value)} />
                </div>
              </div>
              <p className="-mt-2 text-[11px] text-muted-foreground leading-relaxed">
                Must match your government ID for verification. Used only for KYC — never shown publicly.
              </p>
              <div>
                <Label htmlFor="dn">Display name *</Label>
                <Input id="dn" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="pn">Pronouns</Label>
                <Input id="pn" placeholder="she/her, they/them, …" value={pronouns}
                       onChange={(e) => setPronouns(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="dob">Date of birth * (must be {MIN_AGE}+)</Label>
                <Input id="dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea id="bio" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} />
              </div>

              {primaryApp === "fryqnc" && (
                <>
                  <div>
                    <Label htmlFor="gi">Gender identity</Label>
                    <Input id="gi" value={genderIdentity} onChange={(e) => setGenderIdentity(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="so">Sexual orientation</Label>
                    <Input id="so" value={sexualOrientation} onChange={(e) => setSexualOrientation(e.target.value)} />
                  </div>
                </>
              )}

              {primaryApp === "mindlynq" && (
                <div>
                  <Label className="mb-2 block">How you experience the world (optional)</Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {ND_OPTIONS.map((opt) => (
                      <label key={opt} className="flex items-center gap-3 rounded-xl border border-border px-4 py-2.5 cursor-pointer hover:bg-secondary/40">
                        <Checkbox
                          checked={neurodivergence.includes(opt)}
                          onCheckedChange={() => toggleND(opt)}
                        />
                        <span className="text-sm">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Step 2: photos */}
        {step === 2 && (
          <section className="mt-10 space-y-6">
            <div>
              <h2 className="text-xl font-semibold">Add your photos</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                At least one photo. Drag to reorder. Your first photo is your main.
              </p>
            </div>
            <PhotoGrid value={photos} onChange={setPhotos} />
          </section>
        )}

        {/* Step 3: prompts */}
        {step === 3 && primaryApp && (
          <section className="mt-10 space-y-6">
            <div>
              <h2 className="text-xl font-semibold">Pick a few prompts</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Pick at least {MIN_PROMPTS}. They go a long way toward better matches.
              </p>
            </div>
            <PromptsPicker app={primaryApp as import("@/lib/apps").LiveAppId} value={prompts} onChange={setPrompts} />
          </section>
        )}

        {/* Step 4: voice intro */}
        {step === 4 && (
          <section className="mt-10 space-y-6">
            <div>
              <h2 className="text-xl font-semibold">Voice intro</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Optional, but voice intros get noticed. Up to 30 seconds.
              </p>
            </div>
            <VoiceRecorder value={voiceUrl} onChange={setVoiceUrl} />
          </section>
        )}

        {/* Step 5: friendship lane */}
        {step === 5 && (
          <section className="mt-10 space-y-6">
            <div>
              <h2 className="text-xl font-semibold">What are you here for?</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                You can change this anytime in settings.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {LANES.map((l) => {
                const selected = lane === l.value;
                return (
                  <button
                    key={l.value}
                    type="button"
                    onClick={() => setLane(l.value)}
                    className={`text-left rounded-2xl border p-4 transition-colors ${
                      selected ? "border-foreground bg-secondary" : "border-border hover:bg-secondary/40"
                    }`}
                  >
                    <p className="font-semibold">{l.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{l.desc}</p>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Step 6: mentorship */}
        {step === 6 && (
          <section className="mt-10 space-y-6">
            <div>
              <h2 className="text-xl font-semibold">Mentorship in Wavlynq</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Mentors are community-grounded members who share what they've learned —
                career, identity, transitions, recovery, creative practice, and more.
              </p>
            </div>

            <div className="rounded-2xl border border-border p-5 space-y-3 text-sm">
              <p className="font-medium">How it works</p>
              <ol className="list-decimal pl-5 space-y-1.5 text-muted-foreground">
                <li>Tell us your background, specialties, and rate (free is fine).</li>
                <li>We review every application before listing you publicly.</li>
                <li>Members request you with a short note. You accept or decline.</li>
                <li>Sessions happen over video; payouts handled automatically.</li>
              </ol>
              {!isAttuned && (
                <p className="rounded-lg bg-secondary/50 p-3 text-xs text-muted-foreground">
                  Becoming a mentor requires the{" "}
                  <span className="font-medium text-foreground">Attuned</span> tier. You
                  can still browse and request mentors on any tier.
                </p>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {isAttuned && (
                <button
                  type="button"
                  onClick={() => setMentorIntent("apply")}
                  className={`text-left rounded-2xl border p-4 transition-colors ${
                    mentorIntent === "apply"
                      ? "border-foreground bg-secondary"
                      : "border-border hover:bg-secondary/40"
                  }`}
                >
                  <p className="font-semibold">Apply to be a mentor</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    We'll take you straight to the application after onboarding.
                  </p>
                </button>
              )}
              <button
                type="button"
                onClick={() => setMentorIntent("browse")}
                className={`text-left rounded-2xl border p-4 transition-colors ${
                  mentorIntent === "browse"
                    ? "border-foreground bg-secondary"
                    : "border-border hover:bg-secondary/40"
                }`}
              >
                <p className="font-semibold">Find a mentor</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Jump into the mentor browse page when you're done.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setMentorIntent("skip")}
                className={`text-left rounded-2xl border p-4 transition-colors ${
                  mentorIntent === "skip"
                    ? "border-foreground bg-secondary"
                    : "border-border hover:bg-secondary/40"
                }`}
              >
                <p className="font-semibold">Maybe later</p>
                <p className="text-xs text-muted-foreground mt-1">
                  You can always come back from your account.
                </p>
              </button>
            </div>
          </section>
        )}

        <div className="mt-10 flex justify-between items-center">
          <Button variant="ghost" onClick={back} disabled={step === 0}>Back</Button>
          <div className="flex items-center gap-3">
            {isPartner && (
              <Button variant="link" className="text-muted-foreground" onClick={skipOnboarding} disabled={busy}>
                Skip for now
              </Button>
            )}
            {step < totalSteps - 1 ? (
              <Button disabled={!canAdvance} onClick={next}>Continue</Button>
            ) : (
              <Button disabled={busy || !canAdvance} onClick={finish}>
                {busy ? "Saving…" : "Finish"}
              </Button>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Onboarding;
